import { addLeadingSlash } from "@frend-digital/utils";
import { type Metadata } from "next";
import type { ISbStoryData } from "storyblok-js-client";
import { getLang } from "../i18n";
import type { BaseProduct, Category, Page } from "../types";
export type StoryData = ISbStoryData;

export interface LocalizedCategory extends Category {
  languages?: Record<string, Category>;
}

export type GetURL = (
  locale: string,
  path: string,
  segment:
    | { type: "product"; product: BaseProduct }
    | { type: "category"; category: Category }
    | { type: "page"; page: Page }
) => string;

export interface AlternateGeneratorOptions {
  locale: string;
  locales: string[];
  defaultLocale: string;
  getURL: GetURL;
  /**
   * Segments to remove from the URL
   */
  removeSegments?: string[];
  /**
   * Paths to ignore via their segments
   * @example "/configuration/x" will be ignored as ["configuration"] is in ignorePathWithSegments
   */
  ignorePathWithSegments?: string[];
  getProductURL?: GetProductURL;
  categories?: LocalizedCategory[];
}

export interface PageAlternateOptions extends AlternateGeneratorOptions {
  page?: Page;
}

export const removeSegments = (url: string, segments: string[]) => {
  const urlSegments = url.split("/");
  const filteredSegments = urlSegments.filter(
    (segment) => !segments.includes(segment)
  );
  return addLeadingSlash(filteredSegments.join("/"));
};

export const pathHasSegments = (path: string, segments: string[]) => {
  const pathSegments = path.split("/");
  return segments.some((segment) => pathSegments.includes(segment));
};

export const generatePageAlternates = ({
  locale,
  locales,
  page: page,
  getURL,
  defaultLocale,
  removeSegments: segmentsToRemove = [],
  ignorePathWithSegments = [],
}: PageAlternateOptions): Metadata["alternates"] => {
  if (!page) return {};

  const parsePath = (path: string | undefined) => {
    if (!path) return;
    if (pathHasSegments(path, ignorePathWithSegments)) return;
    return removeSegments(path, segmentsToRemove);
  };

  const defaultUri = page.default_full_slug
    ? addLeadingSlash(page.default_full_slug)
    : addLeadingSlash(page.full_slug);

  const parsedDefault = parsePath(defaultUri);
  const parsedFullSlug = parsePath(addLeadingSlash(page.full_slug));

  if (!parsedDefault || !parsedFullSlug) return {};

  const url = getURL(locale, parsedFullSlug, {
    page,
    type: "page",
  });

  const languages = Object.fromEntries(
    locales.map((l) => {
      const lang = getLang(l);
      const foundSlug = page.translated_slugs?.find((s) => s.lang === lang);

      if (!foundSlug)
        return [
          l,
          getURL(l, parsedDefault, {
            type: "page",
            page,
          }),
        ];

      const parsedPath = parsePath(foundSlug.path);
      if (!parsedPath) return [];

      const foundUrl = getURL(l, parsedPath, {
        page,
        type: "page",
      });

      return [l, foundUrl];
    })
  );

  return {
    canonical: url,
    languages: {
      ["x-default"]: getURL(defaultLocale, parsedDefault, {
        page,
        type: "page",
      }),
      [locale]: url,
      ...languages,
    },
  };
};

export type GetProductURL = (
  locale: string,
  product: BaseProduct,
  category?: Category
) => string;

export interface ProductAlternateOptions extends AlternateGeneratorOptions {
  product: BaseProduct;
  categories?: LocalizedCategory[];
  getProductURL?: GetProductURL;
}

export const generateProductAlternates = ({
  product,
  locales,
  locale,
  defaultLocale,
  ignorePathWithSegments,
  removeSegments,
  getURL,
  categories,
  getProductURL,
}: ProductAlternateOptions): Metadata["alternates"] => {
  if (!product.uri) return {};

  const defaultUrl = product.uri;
  const url = getURL(locale, defaultUrl, {
    product,
    type: "product",
  });

  const languages = Object.fromEntries(
    locales.map((locale) => {
      const foundCategory = categories?.find(
        (c) => c.id === product.categoryId
      );

      const noCategoryFoundUrl = getProductURL
        ? getProductURL(locale, product)
        : getURL(locale, product.uri, {
            product,
            type: "product",
          });

      if (!foundCategory) return [locale, noCategoryFoundUrl];

      const url = getProductURL
        ? getProductURL(locale, product, foundCategory)
        : getURL(locale, defaultUrl, {
            product,
            type: "product",
          });
      return [locale, url];
    })
  );

  return {
    canonical: url,
    languages: {
      ["x-default"]: getURL(defaultLocale, defaultUrl, {
        product,
        type: "product",
      }),
      [locale]: url,
      ...languages,
    },
  };
};

export interface CategoryAlternateOptions extends AlternateGeneratorOptions {
  category: LocalizedCategory;
}

/**
 * Generates product listing alternates based on Centra categories
 */
export const generateCategoryAlternates = ({
  category,
  locale,
  locales,
  defaultLocale,
  getURL,
}: CategoryAlternateOptions): Metadata["alternates"] => {
  const url = getURL(locale, category.uri, {
    type: "category",
    category,
  });

  const languages = Object.fromEntries(
    locales.map((locale) => {
      const lang = getLang(locale);
      const foundCategory = category.languages?.[lang];
      if (!foundCategory)
        return [
          locale,
          getURL(locale, category.uri, {
            type: "category",
            category,
          }),
        ];
      return [
        locale,
        getURL(locale, foundCategory.uri, {
          type: "category",
          category: foundCategory,
        }),
      ];
    })
  );

  return {
    canonical: url,
    languages: {
      ["x-default"]: getURL(defaultLocale, category.uri, {
        type: "category",
        category,
      }),
      [locale]: url,
      ...languages,
    },
  };
};
